import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBath, faBed, faCar, faMapMarkerAlt, faPlus, IconDefinition,
} from '@fortawesome/pro-light-svg-icons'

import { useSetState } from '@domain-group/react-hooks'

import {
  Box, Button, Image, Text,
} from '@domain-group/suit-theme'

import type { CampaignComparablePayload } from '@hooks/useCampaignComparable'

import useCampaignComparable from '@hooks/useCampaignComparable'

import type { ModalCallbacks } from '@modals/BrowseCampaignComparablesModal'

import { CampaignComparableSearchModel, CampaignModel } from '@models/types'

import buildCampaignComparable from './utils/buildCampaignComparable'
import defaultImageUrl from './assets/default-comparable.png'

const defaultState = {
  creating: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

type ComparableResultProps = {
  added: boolean,
  browseResult: CampaignComparableSearchModel,
  callbacks: {
    createCampaignComparable: ModalCallbacks['createCampaignComparable'],
  },
  campaign: CampaignModel,
  showImages: boolean,
}

type HandleSubmitParams = {
  browseResult: CampaignComparableSearchModel,
  callbacks: {
    createCampaignComparable: ModalCallbacks['createCampaignComparable'],
    createFn: CampaignComparablePayload['callbacks']['createCampaignComparable'],
    setState: SetState,
  },
  campaign: CampaignModel,
}

const handleSubmit = (params: HandleSubmitParams) => {
  const { browseResult, callbacks, campaign } = params
  const { createCampaignComparable, createFn, setState } = callbacks

  setState({ creating: true })

  // @ts-expect-error TS2739
  const entityParams = buildCampaignComparable({ campaign, comparable: browseResult })

  const createComparablePayload = {
    callbacks: {
      action: createFn,
      afterAction: () => setState({ creating: false }),
    },
    entityParams,
    requestOptions: {
      include_campaign_comparable_image: true,
      include_image_sizes: true,
    },
    toastText: 'Comparable Created',
  }

  createCampaignComparable(createComparablePayload)
}

type RenderFeatureIcon = {
  bar: boolean,
  count: string,
  type: IconDefinition,
}

const renderFeatureIcon = (params: RenderFeatureIcon) => {
  const { bar, count, type } = params

  return (
    <>
      {count || null} <FontAwesomeIcon icon={type} style={{ marginLeft: 5 }} />

      {bar && (
      <Text color="bodyFontLightColor" marginX="small">
        |
      </Text>
      )}
    </>
  )
}

const ComparableResult = (props: ComparableResultProps) => {
  const {
    added, browseResult, callbacks, campaign, showImages,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { creating } = state

  const {
    callbacks: {
      createCampaignComparable: createFn,
    },
  } = useCampaignComparable()

  const {
    address,
    bathrooms,
    bedrooms,
    carparks,
    distance,
    image_url,
    land_area,
    post_code,
    price_guide,
    property_type,
    rpd,
    sale_type,
    sold_date,
    state_name,
    suburb_name,
  } = browseResult

  // @ts-expect-error TS2362
  const formattedDistance = distance ? (distance / 1000).toFixed(2) : null

  return (
    <Box
      flexDirection="Column"
      opacity={added ? '0.5' : null}
    >
      {showImages && (
        <Image
          borderRadius="5px 5px 0 0"
          height={147}
          placeholderUrl={defaultImageUrl}
          url={image_url}
        />
      )}

      <Box
        border="1px solid"
        borderRadius={showImages ? '0 0 5px 5px' : '5px'}
        backgroundColor="white"
        borderColor="lineColor"
        flexDirection="Column"
        marginBottom="large"
        padding="large"
      >
        <Text fontSize="small" marginBottom="small">{address}</Text>

        <Text
          color="bodyFontLightColor"
          fontSize="xsmall"
          marginBottom="medium"
        >
          {suburb_name} {state_name} {post_code} {rpd && `(${rpd})`}
        </Text>

        <Box alignItems="center" fontSize="xsmall" color="bodyFontLightColor" marginBottom="medium">
          {renderFeatureIcon({ count: bedrooms, type: faBed, bar: true })}
          {renderFeatureIcon({ count: bathrooms, type: faBath, bar: true })}
          {renderFeatureIcon({ count: carparks, type: faCar, bar: false })}

          {property_type && (
            <>
              <Text color="bodyFontLightColor" marginX="small">&middot;</Text>
              <Text color="bodyFontLightColor">{property_type}</Text>
            </>
          )}

          {land_area && (
            <>
              &nbsp;- {parseInt(land_area, 10)}m&sup2;
            </>
          )}
        </Box>

        <Text
          color="bodyFontLightColor"
          fontSize="xsmall"
          marginBottom={formattedDistance ? 'medium' : 'large'}
        >
          {sale_type || 'Agents Advise'} - {price_guide || 'Undisclosed'} {sold_date && ` (${sold_date})`}
        </Text>

        {formattedDistance && (
          <Text
            color="bodyFontLightColor"
            fontSize="xsmall"
            marginBottom="large"
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            &nbsp; {formattedDistance} km from target property
          </Text>
        )}

        <Button
          buttonStyle="secondaryEdit"
          disabled={added}
          icon={<FontAwesomeIcon icon={faPlus} />}
          loading={creating}
          onClick={!added
            ? () => handleSubmit({
              callbacks: { ...callbacks, createFn, setState }, campaign, browseResult,
            })
            : undefined}
          size="small"
          //@ts-expect-error
          width="auto"
        >
          {!added ? 'Add Comparable' : 'Comparable Added'}
        </Button>
      </Box>
    </Box>
  )
}

export default ComparableResult
